import { useMemo, useRef } from 'react';
import type { MouseEvent, PointerEvent, TouchEvent } from 'react';

interface UseSwipeTabsOptions {
  disabled?: boolean;
  threshold?: number;
}

interface SwipeState {
  pointerId: number | null;
  startX: number;
  startY: number;
  didSwipe: boolean;
}

export function useSwipeTabs<T extends string>(
  tabs: readonly T[],
  activeTab: T,
  onChange: (tab: T) => void,
  options: UseSwipeTabsOptions = {},
) {
  const stateRef = useRef<SwipeState | null>(null);
  const suppressClickRef = useRef(false);
  const threshold = options.threshold ?? 48;

  return useMemo(() => {
    const moveBy = (step: -1 | 1) => {
      const index = tabs.indexOf(activeTab);
      if (index < 0) return;

      const next = tabs[index + step];
      if (next) onChange(next);
    };

    const clear = () => {
      stateRef.current = null;
    };

    const start = (startX: number, startY: number, pointerId: number | null = null) => {
      stateRef.current = {
        pointerId,
        startX,
        startY,
        didSwipe: false,
      };
    };

    const move = (clientX: number, clientY: number) => {
      const state = stateRef.current;
      if (!state) return;

      const dx = clientX - state.startX;
      const dy = clientY - state.startY;
      if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy) * 1.2) {
        state.didSwipe = true;
      }
    };

    const end = (clientX: number, clientY: number) => {
      const state = stateRef.current;
      if (!state) return;

      const dx = clientX - state.startX;
      const dy = clientY - state.startY;
      const isHorizontalSwipe = Math.abs(dx) >= threshold && Math.abs(dx) > Math.abs(dy) * 1.2;

      if (isHorizontalSwipe) {
        suppressClickRef.current = true;
        window.setTimeout(() => {
          suppressClickRef.current = false;
        }, 350);
        moveBy(dx < 0 ? 1 : -1);
      }
      clear();
    };

    return {
      onPointerDown: (event: PointerEvent<HTMLElement>) => {
        if (options.disabled) return;
        start(event.clientX, event.clientY, event.pointerId);
      },
      onPointerMove: (event: PointerEvent<HTMLElement>) => {
        const state = stateRef.current;
        if (!state || state.pointerId !== event.pointerId) return;
        move(event.clientX, event.clientY);
      },
      onPointerUp: (event: PointerEvent<HTMLElement>) => {
        const state = stateRef.current;
        if (!state || state.pointerId !== event.pointerId) return;
        end(event.clientX, event.clientY);
      },
      onPointerCancel: clear,
      onTouchStart: (event: TouchEvent<HTMLElement>) => {
        if (options.disabled || event.touches.length !== 1) return;
        const touch = event.touches[0];
        start(touch.clientX, touch.clientY);
      },
      onTouchMove: (event: TouchEvent<HTMLElement>) => {
        if (event.touches.length !== 1) return;
        const touch = event.touches[0];
        move(touch.clientX, touch.clientY);
      },
      onTouchEnd: (event: TouchEvent<HTMLElement>) => {
        const touch = event.changedTouches[0];
        if (!touch) return;
        end(touch.clientX, touch.clientY);
      },
      onTouchCancel: clear,
      onClickCapture: (event: MouseEvent<HTMLElement>) => {
        if (!suppressClickRef.current) return;
        event.preventDefault();
        event.stopPropagation();
        suppressClickRef.current = false;
      },
    };
  }, [activeTab, onChange, options.disabled, threshold, tabs]);
}
